/**
 * HoldOn Product Section JavaScript
 * Handles product interactions, quantity updates, and image gallery
 */

class HoldOnProduct {
  constructor() {
    this.init();
  }

  init() {
    this.setupQuantityControls();
    this.setupImageGallery();
    this.setupAddToCart();
    this.setupCollapsibleSections();
    // Initialize CTA price on load based on default quantity
    const quantityValue = document.querySelector('.holdon-product__quantity-value');
    const initialQty = parseInt(quantityValue?.textContent) || 1;
    this.updateCartButton(initialQty);
  }

  setupQuantityControls() {
    const quantityBtns = document.querySelectorAll('.holdon-product__quantity-btn');
    const quantityValue = document.querySelector('.holdon-product__quantity-value');
    
    if (!quantityValue) return;
    
    quantityBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        let currentValue = parseInt(quantityValue.textContent) || 1;
        
        if (btn.classList.contains('holdon-product__quantity-btn--minus')) {
          if (currentValue > 1) {
            currentValue--;
          }
        } else if (btn.classList.contains('holdon-product__quantity-btn--plus')) {
          currentValue++;
        }
        
        quantityValue.textContent = currentValue;
        this.updateCartButton(currentValue);
      });
    });
  }

  setupImageGallery() {
    const main = document.querySelector('.holdon-product__main-img');
    const thumbs = Array.from(document.querySelectorAll('.holdon-product__thumbnail:not(.holdon-product__thumbnail--placeholder)'));
    const mainMedia = document.querySelector('.holdon-product__main-image');
    
    if (!main || !thumbs.length) return;

    // Build media arrays from thumbnails
    const media = thumbs
      .map((t) => ({
        src: t.getAttribute('data-src'),
        zoomSrc: t.getAttribute('data-zoom-src'),
      }))
      .filter((m) => m.src);

    let currentIndex = 0;
    let isMouseOverImage = false;
    let wasArrowClicked = false;

    function updateImage(index) {
      if (!media[index]) return;

      currentIndex = index;
      const { src, zoomSrc } = media[index];

      // Update main image
      main.src = src;
      main.setAttribute('data-zoom-src', zoomSrc);

      // Hide zoom overlay when switching images
      const overlay = document.querySelector('.holdon-product__zoom-overlay');
      if (overlay) {
        overlay.style.display = 'none';
      }

      // Update active thumbnail
      thumbs.forEach((thumb, i) => {
        thumb.classList.remove('is-active', 'holdon-product__thumbnail--active');
        if (i === index) {
          thumb.classList.add('is-active', 'holdon-product__thumbnail--active');
        }
      });
    }

    // Thumbnail clicks
    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        wasArrowClicked = true; // Same behavior as arrows
        updateImage(index);
      });
    });

    // Simple overlay zoom functionality
    function initializeZoom() {
      const overlay = document.querySelector('.holdon-product__zoom-overlay');
      if (!overlay || 'ontouchstart' in window) return;

      mainMedia.addEventListener('mouseenter', () => {
        isMouseOverImage = true;
        // Only activate zoom if arrow wasn't just clicked
        if (!wasArrowClicked) {
          const zoomSrc = main.getAttribute('data-zoom-src');
          if (zoomSrc) {
            overlay.style.display = 'block';
            overlay.style.backgroundImage = `url(${zoomSrc})`;
            // Match the main image's object-fit: cover behavior
            overlay.style.backgroundSize = 'cover';
            overlay.style.backgroundPosition = 'center center';
            overlay.style.transform = 'scale(2)';
            overlay.style.transformOrigin = 'center center';
          }
        }
      });

      mainMedia.addEventListener('mouseleave', () => {
        isMouseOverImage = false;
        wasArrowClicked = false; // Reset flag when leaving image area
        overlay.style.display = 'none';
      });

      mainMedia.addEventListener('mousemove', (e) => {
        // If arrow was clicked and zoom is hidden, check if we should reactivate zoom
        if (wasArrowClicked && overlay.style.display === 'none') {
          const target = e.target;
          // Only reactivate zoom if cursor is over the main image, not arrows
          if (target === main) {
            wasArrowClicked = false;
            const zoomSrc = main.getAttribute('data-zoom-src');
            if (zoomSrc) {
              overlay.style.display = 'block';
              overlay.style.backgroundImage = `url(${zoomSrc})`;
              overlay.style.backgroundSize = 'cover';
              overlay.style.backgroundPosition = 'center center';
              overlay.style.transform = 'scale(2)';
              overlay.style.transformOrigin = 'center center';
            }
          }
        }

        if (overlay.style.display === 'none') return;

        const rect = mainMedia.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate percentage position of cursor relative to the container
        const xPercent = (x / rect.width) * 100;
        const yPercent = (y / rect.height) * 100;

        // Update transform origin to follow mouse cursor for zoom effect
        overlay.style.transformOrigin = `${xPercent}% ${yPercent}%`;
      });
    }

    // Initialize zoom after first image loads
    if (main.complete) {
      initializeZoom();
    } else {
      main.addEventListener('load', initializeZoom);
    }

    // Set first thumbnail as active by default
    if (thumbs[0]) {
      updateImage(0);
    }
  }

  setupAddToCart() {
    const ctaBtn = document.querySelector('.holdon-product__cta-btn');
    const quantityValue = document.querySelector('.holdon-product__quantity-value');
    
    if (!ctaBtn) return;
    
    ctaBtn.addEventListener('click', (e) => {
      e.preventDefault();
      
      const quantity = parseInt(quantityValue?.textContent) || 1;
      const variantId = ctaBtn.getAttribute('data-variant-id');
      
      if (variantId) {
        this.addToCart(variantId, quantity);
      } else {
        // Fallback for demo/preview
        this.showNotification('Product added to cart!', 'success');
      }
    });
  }

  setupCollapsibleSections() {
    const details = document.querySelectorAll('.holdon-product__details');
    
    details.forEach(detail => {
      const summary = detail.querySelector('.holdon-product__summary');
      
      if (summary) {
        summary.addEventListener('click', (e) => {
          // Add smooth animation
          if (detail.open) {
            const content = detail.querySelector('.holdon-product__details-content');
            if (content) {
              content.style.animation = 'slideUp 0.3s ease';
            }
          }
        });
      }
    });
  }

  getProductId() {
    // Try to get product ID from various sources
    const productData = document.querySelector('[data-product-id]');
    if (productData) {
      return productData.getAttribute('data-product-id');
    }
    
    // Try to get from URL
    const pathParts = window.location.pathname.split('/');
    const productIndex = pathParts.indexOf('products');
    if (productIndex !== -1 && pathParts[productIndex + 1]) {
      // This would need to be converted to actual product ID via Shopify API
      return null;
    }
    
    return null;
  }

  async addToCart(variantId, quantity) {
    try {
      this.showLoading(true);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity
          }]
        })
      });
      
      const data = await response.json();
      
      if (response.ok && !data.status) {
        // Update cart count and refresh cart drawer
        await this.updateCartCount();
        await this.refreshCartDrawer();
        
        // Open cart drawer if it exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          setTimeout(() => {
            cartDrawer.open();
          }, 300);
        }
      } else {
        // Handle Shopify cart errors
        const errorMessage = data.message || data.description || 'Failed to add to cart';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      // Removed notification - just log the error
    } finally {
      this.showLoading(false);
    }
  }

  updateCartButton(quantity) {
    const ctaBtnWrapper = document.querySelector('.holdon-product__cta-btn');
    const ctaBtn = document.querySelector('.holdon-product__cta-btn span');
    if (!ctaBtnWrapper || !ctaBtn) return;

    // Get unit price in IDR (no decimals), stored as digits only string, e.g., "135000"
    const unitPriceStr = ctaBtnWrapper.getAttribute('data-unit-price-idr');
    const unitPrice = parseInt(unitPriceStr, 10);

    // Fallback to 50000 if not available
    const safeUnitPrice = Number.isFinite(unitPrice) ? unitPrice : 50000;
    const total = safeUnitPrice * (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);

    // Format with thousand separators using dots (IDR style) and no decimals
    const formatted = total
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    ctaBtn.textContent = `RESERVE NOW - IDR ${formatted}`;
  }

  showLoading(show) {
    const ctaBtn = document.querySelector('.holdon-product__cta-btn');
    if (ctaBtn) {
      if (show) {
        ctaBtn.style.opacity = '0.7';
        ctaBtn.style.cursor = 'not-allowed';
        ctaBtn.disabled = true;
      } else {
        ctaBtn.style.opacity = '1';
        ctaBtn.style.cursor = 'pointer';
        ctaBtn.disabled = false;
      }
    }
  }

  showNotification(message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `holdon-notification holdon-notification--${type}`;
    notification.textContent = message;
    
    // Style the notification
    Object.assign(notification.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '16px 24px',
      borderRadius: '8px',
      color: 'white',
      fontFamily: "'Delight', Arial, sans-serif",
      fontSize: '14px',
      fontWeight: '600',
      zIndex: '10000',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease',
      backgroundColor: type === 'success' ? '#4CAF50' : type === 'error' ? '#f44336' : '#2196F3'
    });
    
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);
    
    // Remove after delay
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 3000);
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count in header if exists
      const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }

  async refreshCartDrawer() {
    try {
      // Fetch the cart drawer section
      const response = await fetch('/?section_id=cart-drawer');
      const html = await response.text();
      
      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newCartDrawer = doc.querySelector('cart-drawer');
      
      if (newCartDrawer) {
        const currentCartDrawer = document.querySelector('cart-drawer');
        if (currentCartDrawer) {
          // Replace the entire cart drawer content
          currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
          
          // Remove the empty class if it exists
          currentCartDrawer.classList.remove('is-empty');
        }
      }
    } catch (error) {
      console.error('Failed to refresh cart drawer:', error);
      // Fallback: reload the page
      window.location.reload();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new HoldOnProduct();
});

// Add CSS animation for slideUp
const style = document.createElement('style');
style.textContent = `
  @keyframes slideUp {
    from {
      opacity: 1;
      max-height: 1000px;
    }
    to {
      opacity: 0;
      max-height: 0;
    }
  }
`;
document.head.appendChild(style);
